/**
 * The dates a population / IPE proof covers, compared with the testing period. A statement prints its dates
 * without a year ("05JUN"): the AI service then returns a yearless range ({from: "--06-05", no_year: true}) and the
 * year is taken from the testing period here.
 */

export type DateRange = { from?: string | null; to?: string | null; no_year?: boolean; [key: string]: unknown } | null | undefined;
export type PeriodResult = 'within' | 'outside' | 'unknown';

const day = (d?: string | null) => (d ? String(d).slice(0, 10) : null);

/** A range with full ISO dates, the year of a yearless one taken from the testing period; null when unusable. */
export function completeYear(range: DateRange, start?: string | null, end?: string | null): { from: string; to: string; yearFromPeriod?: boolean } | null {
  if (!range?.from || !range?.to) return null;
  // Only yearless values ("--06-05") are completed; a full date is used as it is, whatever the flag says.
  if (!String(range.from).startsWith('--')) {
    return { from: day(range.from) as string, to: day(range.to) as string };
  }
  const s = day(start);
  if (!s) return null;
  const e = day(end);
  const fill = (md: string, year: number) => {
    const [month, dd] = md.replace(/^-+/, '').split('-');
    return `${year}-${month}-${dd}`;
  };
  let year = Number(s.slice(0, 4));
  let from = fill(String(range.from), year);
  if (e && from < s && e.slice(0, 4) !== s.slice(0, 4)) { year = Number(e.slice(0, 4)); from = fill(String(range.from), year); }
  let to = fill(String(range.to), year);
  if (to < from) to = fill(String(range.to), year + 1);   // the range crosses a year end
  return { from, to, yearFromPeriod: true };
}

/** "2025-06-05 – 2025-06-27" (one date when they are equal), "—" when unknown. */
export function formatRange(r?: { from?: string | null; to?: string | null } | null): string {
  if (!r?.from || !r?.to) return '—';
  return r.from === r.to ? r.from : `${r.from} – ${r.to}`;
}

/** Whether every known range falls inside the testing period. */
export function periodResult(ranges: Array<{ from: string; to: string } | null>, start?: string | null, end?: string | null): PeriodResult {
  const known = ranges.filter(Boolean) as Array<{ from: string; to: string }>;
  const s = day(start);
  const e = day(end);
  if (!known.length || !s || !e) return 'unknown';
  return known.every((r) => r.from >= s && r.to <= e) ? 'within' : 'outside';
}
